import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTaskStatus } from 'api/connectors';
import { extractApiError } from 'api/errors';

// MUI greys out every disabled button; an outcome keeps its colour.
const outcomeSx = (color) => ({
  '&.Mui-disabled': { color: `${color}.contrastText`, bgcolor: `${color}.main` },
});

/**
 * A button that calls onTrigger() on click — expecting Promise<{ task_id }> —
 * polls /api/v1/tasks/{task_id}/ until done, then shows inline success/error.
 * A start the API refuses shows as an error too, with the reason in a tooltip.
 * Button props style the idle button; the other states override them.
 */
export default function AsyncTaskButton({
  onTrigger,
  label,
  loadingLabel,
  onSuccess,
  onError,
  ...buttonProps
}) {
  const { t: translate } = useTranslation();
  const [starting, setStarting]   = useState(false);
  const [taskId, setTaskId]       = useState(null);
  const [done, setDone]           = useState(null); // 'success' | 'error' | null
  const [reason, setReason]       = useState(null); // why the API refused to start

  const { data: taskData } = useTaskStatus(taskId);

  React.useEffect(() => {
    if (!taskData) return;
    if (taskData.status === 'SUCCESS') {
      setDone('success');
      setTaskId(null);
      onSuccess?.(taskData.result);
      setTimeout(() => setDone(null), 4000);
    } else if (taskData.status === 'FAILURE') {
      setDone('error');
      setTaskId(null);
      onError?.(taskData.error);
      setTimeout(() => setDone(null), 6000);
    }
  }, [taskData, onSuccess, onError]);

  const isBusy = starting || !!taskId;

  const handleClick = async () => {
    setDone(null);
    setStarting(true);
    try {
      const result = await onTrigger();
      setTaskId(result.task_id);
    } catch (err) {
      setReason(extractApiError(err, translate('shell.taskStartFailed')));
      setDone('error');
      setTimeout(() => {
        setDone(null);
        setReason(null);
      }, 6000);
    } finally {
      setStarting(false);
    }
  };

  if (done === 'success') {
    return (
      <Button
        {...buttonProps}
        variant="contained"
        color="success"
        startIcon={<CheckIcon />}
        disabled
        sx={outcomeSx('success')}
      >{translate('shell.success')}</Button>
    );
  }

  if (done === 'error') {
    const failed = (
      <Button
        {...buttonProps}
        variant="contained"
        color="error"
        startIcon={<ErrorOutlineIcon />}
        disabled
        sx={outcomeSx('error')}
      >{translate('shell.failed')}</Button>
    );
    if (!reason) return failed;
    // A disabled button fires no mouse events, so the tooltip listens on a wrapper.
    return (
      <Tooltip title={reason}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {failed}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Button
      {...buttonProps}
      onClick={handleClick}
      disabled={isBusy || buttonProps.disabled}
      startIcon={
        isBusy
          ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
          : buttonProps.startIcon ?? <PlayArrowIcon fontSize="small" />
      }
    >
      {isBusy ? loadingLabel || translate('shell.running') : label}
    </Button>
  );
}
